import { Formik } from "formik";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { FormattedMessage } from "react-intl";
import { toast } from "react-toastify";
import { useUser } from "../../contexts/UserContext";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Avatar,
  Grid,
  Paper,
} from "@mui/material";

const Login: React.FC = () => {
  const [loginData] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const { userId, handleLogin } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      navigate("/profile");
    }
  }, [navigate, userId]);

  const validationSchema = Yup.object().shape({
    username: Yup.string().required("This field is required!"),
    password: Yup.string().required("This field is required!"),
  });

  const loginHandler = async (formValue: {
    username: string;
    password: string;
  }) => {
    const { username, password } = formValue;

    setMessage("");
    setLoading(true);
    try {
      await handleLogin(username, password);
      setLoading(false);
    } catch (error) {
      let resMessage = "An error occurred on login";

      if (error instanceof Error) {
        resMessage = error.message;
      }

      toast.warn(resMessage, {
        position: "bottom-right",
      });

      if (error && typeof error === "object" && "response" in error) {
        const httpError = error as { response: { data: { message: string } } };
        if (httpError.response && httpError.response.data) {
          resMessage = httpError.response.data.message;
        }
      }
      setLoading(false);
      setMessage(resMessage);
    }
  };

  return (
    <Grid container width="97vw" height="90vh" max-width="100%" justifyContent="center" alignItems="center">
      <Grid item xs={12} sm={8} md={5}>
        <Paper elevation={4} sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Avatar
              alt="profile-img"
              src="//ssl.gstatic.com/accounts/ui/avatar_2x.png"
              sx={{ width: 80, height: 80, mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              <FormattedMessage id="log-in" />
            </Typography>

            <Formik
              initialValues={loginData}
              validationSchema={validationSchema}
              onSubmit={loginHandler}
            >
              {({ handleSubmit, values, handleChange, touched, errors }) => (
                <form onSubmit={handleSubmit}>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label={<FormattedMessage id="username" />}
                      name="username"
                      id="username"
                      value={values.username}
                      onChange={handleChange}
                      error={Boolean(touched.username && errors.username)}
                      helperText={
                        touched.username && errors.username
                          ? errors.username
                          : ""
                      }
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      type="password"
                      id="password"
                      label={<FormattedMessage id="password" />}
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      error={Boolean(touched.password && errors.password)}
                      helperText={
                        touched.password && errors.password
                          ? errors.password
                          : ""
                      }
                    />
                  </Box>

                  {message && (
                    <Box mb={2}>
                      <Typography color="error" variant="body2">
                        {message}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    type="submit"
                    id="login"
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <FormattedMessage id="log-in" />
                    )}
                  </Button>
                </form>
              )}
            </Formik>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Login;
